import { parseJsonWithNativeSafeNumbers, stringifyLosslessJson } from '@pillar-portal/api-types';
import type { ReactElement } from 'react';
import { z } from 'zod';
import { AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    AllowedWebsiteSchemaUnmanaged,
    AllowedWebsiteTemplateSystemIdSchema,
    defaultIpgConfigById,
    defaultIpwConfigById,
    IpWebsiteType,
    NumericIpgSystemIdSchema,
    NumericIpwSystemIdSchema,
    SessionPersistenceType,
    ThirdPartySystemId,
    UrlPatternsRuleAction,
    UrlPatternsRuleSchema,
    type AllowedWebsiteTemplateSystemId,
    type CreateAllowedWebsiteTemplateRequest,
    type IpgSystemId,
    type IpwSystemId,
    type AllowedWebsiteTemplateListItem,
    type AllowedWebsiteTemplateTransfer,
    type UnmanagedAllowedWebsite,
    type UpdateAllowedWebsiteTemplateRequest,
    type UrlPatternsRule,
} from '@pillar-portal/api-types';
import { AllowedWebsiteTemplateConfigForm } from './AllowedWebsiteTemplateConfigForm';

const UrlPatternsRuleArraySchema: z.ZodType<UrlPatternsRule[]> = z.array(UrlPatternsRuleSchema);

export const AllowedWebsiteTemplateNamespaceMode = {
    GATEWAY: 'GATEWAY',
    SYSTEM_OF_RECORD: 'SYSTEM_OF_RECORD',
    GENERIC: 'GENERIC',
} as const;

export type AllowedWebsiteTemplateNamespaceMode =
    (typeof AllowedWebsiteTemplateNamespaceMode)[keyof typeof AllowedWebsiteTemplateNamespaceMode];

interface TemplateSystemOption<SystemId extends number> {
    readonly systemId: SystemId;
    readonly label: string;
}

const GatewayTemplateSystemOptions: readonly TemplateSystemOption<IpgSystemId>[] = Object.values(
    defaultIpgConfigById
)
    .map((config) => ({
        systemId: NumericIpgSystemIdSchema.parse(config.ipg_id),
        label: config.name,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
const SystemOfRecordTemplateSystemOptions: readonly TemplateSystemOption<IpwSystemId>[] =
    Object.values(defaultIpwConfigById)
        .map((config) => ({
            systemId: NumericIpwSystemIdSchema.parse(config.ipw_id),
            label: config.name,
        }))
        .sort((left, right) => left.label.localeCompare(right.label));
const GatewayTemplateSystemIdSet: ReadonlySet<number> = new Set(
    GatewayTemplateSystemOptions.map((option) => option.systemId)
);
const SystemOfRecordTemplateSystemIdSet: ReadonlySet<number> = new Set(
    SystemOfRecordTemplateSystemOptions.map((option) => option.systemId)
);
function getDefaultTemplateSystemId<SystemId extends number>(
    options: readonly TemplateSystemOption<SystemId>[],
    label: string
): SystemId {
    const defaultOption = options[0];
    if (defaultOption === undefined) {
        throw new Error(`No ${label} systems are configured.`);
    }
    return defaultOption.systemId;
}
const DefaultGatewayTemplateSystemId: IpgSystemId = getDefaultTemplateSystemId(
    GatewayTemplateSystemOptions,
    'gateway'
);
const DefaultSystemOfRecordTemplateSystemId: IpwSystemId = getDefaultTemplateSystemId(
    SystemOfRecordTemplateSystemOptions,
    'system of record'
);

const AllowedWebsiteTemplateNamespaceOptions: readonly {
    readonly mode: AllowedWebsiteTemplateNamespaceMode;
    readonly label: string;
}[] = [
    {
        mode: AllowedWebsiteTemplateNamespaceMode.GATEWAY,
        label: 'Gateway',
    },
    {
        mode: AllowedWebsiteTemplateNamespaceMode.SYSTEM_OF_RECORD,
        label: 'System of record',
    },
    {
        mode: AllowedWebsiteTemplateNamespaceMode.GENERIC,
        label: 'Generic',
    },
];

export interface AllowedWebsiteTemplateEditorState {
    templateName: string;
    namespaceMode: AllowedWebsiteTemplateNamespaceMode;
    ipxSystemIdText: string;
    websiteName: string;
    websiteUrl: string;
    allowedUrlPatternsText: string;
    sessionPersistence: SessionPersistenceType;
    promoteSessionCookies: boolean;
    fetchDefaultAction: UrlPatternsRuleAction;
    urlPatternsRulesJson: string;
    iframeDefaultAction: UrlPatternsRuleAction;
    iframeUrlPatternsJson: string;
}

export interface AllowedWebsiteTemplateValidationIssue {
    fieldId: string;
    label: string;
    message: string;
}

export const DefaultAllowedWebsiteTemplateEditorState: AllowedWebsiteTemplateEditorState = {
    templateName: '',
    namespaceMode: AllowedWebsiteTemplateNamespaceMode.GENERIC,
    ipxSystemIdText: '0',
    websiteName: '',
    websiteUrl: '',
    allowedUrlPatternsText: '',
    sessionPersistence: SessionPersistenceType.PERSISTENT,
    promoteSessionCookies: false,
    fetchDefaultAction: UrlPatternsRuleAction.ALLOW,
    urlPatternsRulesJson: '[]',
    iframeDefaultAction: UrlPatternsRuleAction.ALLOW,
    iframeUrlPatternsJson: '[]',
};

function parseIntegerField(value: string, label: string): number {
    const trimmedValue: string = value.trim();
    if (!/^\d+$/.test(trimmedValue)) {
        throw new Error(`${label} must be a non-negative integer.`);
    }
    return Number.parseInt(trimmedValue, 10);
}

function parseTemplateSystemIdField(value: string, label: string): AllowedWebsiteTemplateSystemId {
    const numericValue: number = parseIntegerField(value, label);
    const parseResult = AllowedWebsiteTemplateSystemIdSchema.safeParse(numericValue);
    if (!parseResult.success) {
        throw new Error(`${label} is not supported.`);
    }
    return parseResult.data;
}

function parseGatewaySystemIdField(value: string): IpgSystemId {
    const systemId: AllowedWebsiteTemplateSystemId = parseTemplateSystemIdField(value, 'Gateway');
    const selectedOption: TemplateSystemOption<IpgSystemId> | undefined =
        GatewayTemplateSystemOptions.find((option) => option.systemId === systemId);
    if (selectedOption === undefined) {
        throw new Error('Gateway is not supported.');
    }
    return selectedOption.systemId;
}

function parseSystemOfRecordSystemIdField(value: string): IpwSystemId {
    const systemId: AllowedWebsiteTemplateSystemId = parseTemplateSystemIdField(
        value,
        'System of record'
    );
    const selectedOption: TemplateSystemOption<IpwSystemId> | undefined =
        SystemOfRecordTemplateSystemOptions.find((option) => option.systemId === systemId);
    if (selectedOption === undefined) {
        throw new Error('System of record is not supported.');
    }
    return selectedOption.systemId;
}

function resolveTemplateSystemId(
    state: AllowedWebsiteTemplateEditorState
): AllowedWebsiteTemplateSystemId {
    if (state.namespaceMode === AllowedWebsiteTemplateNamespaceMode.GENERIC) {
        return ThirdPartySystemId.GENERIC;
    }
    if (state.namespaceMode === AllowedWebsiteTemplateNamespaceMode.GATEWAY) {
        return parseGatewaySystemIdField(state.ipxSystemIdText);
    }
    return parseSystemOfRecordSystemIdField(state.ipxSystemIdText);
}

function namespaceModeForSystemId(systemId: number): AllowedWebsiteTemplateNamespaceMode {
    if (systemId === ThirdPartySystemId.GENERIC) {
        return AllowedWebsiteTemplateNamespaceMode.GENERIC;
    }
    if (GatewayTemplateSystemIdSet.has(systemId)) {
        return AllowedWebsiteTemplateNamespaceMode.GATEWAY;
    }
    if (SystemOfRecordTemplateSystemIdSet.has(systemId)) {
        return AllowedWebsiteTemplateNamespaceMode.SYSTEM_OF_RECORD;
    }
    throw new Error(`Allowed website system id ${systemId} is not supported.`);
}

function defaultSystemIdTextForNamespaceMode(
    namespaceMode: AllowedWebsiteTemplateNamespaceMode
): string {
    if (namespaceMode === AllowedWebsiteTemplateNamespaceMode.GATEWAY) {
        return String(DefaultGatewayTemplateSystemId);
    }
    if (namespaceMode === AllowedWebsiteTemplateNamespaceMode.SYSTEM_OF_RECORD) {
        return String(DefaultSystemOfRecordTemplateSystemId);
    }
    return String(ThirdPartySystemId.GENERIC);
}

function parsePatternLines(value: string): string[] {
    return value
        .split(/\r?\n/u)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
}

function resolveAllowedUrlPatterns(state: AllowedWebsiteTemplateEditorState): string[] {
    const allowedUrlPatterns: string[] = parsePatternLines(state.allowedUrlPatternsText);
    return allowedUrlPatterns.length > 0 ? allowedUrlPatterns : [state.websiteUrl.trim()];
}

function parseRuleJson(value: string, label: string): UrlPatternsRule[] {
    let parsedValue: unknown;
    try {
        parsedValue = parseJsonWithNativeSafeNumbers(value);
    } catch {
        throw new Error(`${label} must be valid JSON.`);
    }

    const parseResult = UrlPatternsRuleArraySchema.safeParse(parsedValue);
    if (!parseResult.success) {
        throw new Error(`${label} must be an array of URL pattern rule objects.`);
    }
    return parseResult.data;
}

export function buildAllowedWebsiteTemplateCreateRequest(
    state: AllowedWebsiteTemplateEditorState
): CreateAllowedWebsiteTemplateRequest {
    const ipxSystemId: AllowedWebsiteTemplateSystemId = resolveTemplateSystemId(state);
    const websiteConfigParseResult = AllowedWebsiteSchemaUnmanaged.safeParse({
        type: IpWebsiteType.UNMANAGED,
        system_id: ipxSystemId,
        name: state.websiteName.trim(),
        url: state.websiteUrl.trim(),
        allowedUrlPatterns: resolveAllowedUrlPatterns(state),
        sessionPersistence: state.sessionPersistence,
        promoteSessionCookies: state.promoteSessionCookies,
        urlPatternsRules: parseRuleJson(state.urlPatternsRulesJson, 'Fetch URL pattern rules'),
        fetchDefaultAction: state.fetchDefaultAction,
        iframeUrlPatterns: parseRuleJson(state.iframeUrlPatternsJson, 'Iframe URL pattern rules'),
        iframeDefaultAction: state.iframeDefaultAction,
    });

    if (!websiteConfigParseResult.success) {
        throw new Error('Website policy validation failed.');
    }

    return {
        ipx_system_id: ipxSystemId,
        name: state.templateName.trim(),
        website_config: websiteConfigParseResult.data,
    };
}

export function buildAllowedWebsiteTemplateUpdateRequest(
    state: AllowedWebsiteTemplateEditorState,
    expectedWebsiteConfig: UnmanagedAllowedWebsite,
    expectedActiveMerchantCount: number
): UpdateAllowedWebsiteTemplateRequest {
    const createRequest: CreateAllowedWebsiteTemplateRequest =
        buildAllowedWebsiteTemplateCreateRequest(state);

    return {
        ipx_system_id: createRequest.ipx_system_id,
        name: createRequest.name,
        website_config: createRequest.website_config,
        expected_website_config: expectedWebsiteConfig,
        expected_active_merchant_count: expectedActiveMerchantCount,
    };
}

export function buildAllowedWebsiteTemplateEditorStateFromTemplate(
    template: AllowedWebsiteTemplateListItem
): AllowedWebsiteTemplateEditorState {
    return buildAllowedWebsiteTemplateEditorState(template);
}

export function buildAllowedWebsiteTemplateEditorStateFromTransfer(
    transfer: AllowedWebsiteTemplateTransfer
): AllowedWebsiteTemplateEditorState {
    return buildAllowedWebsiteTemplateEditorState(transfer);
}

function buildAllowedWebsiteTemplateEditorState(
    template: Pick<AllowedWebsiteTemplateListItem, 'ipx_system_id' | 'name' | 'website_config'>
): AllowedWebsiteTemplateEditorState {
    return {
        templateName: template.name,
        namespaceMode: namespaceModeForSystemId(template.ipx_system_id),
        ipxSystemIdText: String(template.ipx_system_id),
        websiteName: template.website_config.name,
        websiteUrl: template.website_config.url,
        allowedUrlPatternsText: template.website_config.allowedUrlPatterns.join('\n'),
        sessionPersistence: template.website_config.sessionPersistence,
        promoteSessionCookies: template.website_config.promoteSessionCookies,
        fetchDefaultAction: template.website_config.fetchDefaultAction,
        urlPatternsRulesJson: stringifyLosslessJson(
            template.website_config.urlPatternsRules,
            null,
            2
        ),
        iframeDefaultAction: template.website_config.iframeDefaultAction,
        iframeUrlPatternsJson: stringifyLosslessJson(
            template.website_config.iframeUrlPatterns,
            null,
            2
        ),
    };
}

export function getAllowedWebsiteTemplateValidationIssues(
    state: AllowedWebsiteTemplateEditorState
): AllowedWebsiteTemplateValidationIssue[] {
    const issues: AllowedWebsiteTemplateValidationIssue[] = [];

    if (state.templateName.trim().length === 0) {
        issues.push({
            fieldId: 'template_name',
            label: 'Template name',
            message: 'Template name is required.',
        });
    }
    let ipxSystemId: number = ThirdPartySystemId.GENERIC;
    try {
        ipxSystemId = resolveTemplateSystemId(state);
    } catch (error: unknown) {
        const message: string =
            error instanceof Error ? error.message : 'Allowed website is invalid.';
        issues.push({
            fieldId: 'ipx_system_id',
            label: 'Allowed website',
            message,
        });
    }
    if (state.websiteName.trim().length === 0) {
        issues.push({
            fieldId: 'website_name',
            label: 'Website name',
            message: 'Website name is required.',
        });
    }

    const websiteConfigParseResult = AllowedWebsiteSchemaUnmanaged.safeParse({
        type: IpWebsiteType.UNMANAGED,
        system_id: ipxSystemId,
        name: state.websiteName.trim(),
        url: state.websiteUrl.trim(),
        allowedUrlPatterns: resolveAllowedUrlPatterns(state),
        sessionPersistence: state.sessionPersistence,
        promoteSessionCookies: state.promoteSessionCookies,
        urlPatternsRules: safeParseRuleJson(state.urlPatternsRulesJson),
        fetchDefaultAction: state.fetchDefaultAction,
        iframeUrlPatterns: safeParseRuleJson(state.iframeUrlPatternsJson),
        iframeDefaultAction: state.iframeDefaultAction,
    });

    if (!websiteConfigParseResult.success) {
        for (const issue of websiteConfigParseResult.error.issues) {
            const fieldId: string = getWebsitePolicyFieldId(issue.path);
            issues.push({
                fieldId,
                label: getWebsitePolicyFieldLabel(fieldId),
                message: issue.message,
            });
        }
    }

    if (safeParseRuleJson(state.urlPatternsRulesJson) === null) {
        issues.push({
            fieldId: 'url_patterns_rules',
            label: 'Fetch URL pattern rules',
            message: 'Fetch URL pattern rules must be valid rule JSON.',
        });
    }
    if (safeParseRuleJson(state.iframeUrlPatternsJson) === null) {
        issues.push({
            fieldId: 'iframe_url_patterns',
            label: 'Iframe URL pattern rules',
            message: 'Iframe URL pattern rules must be valid rule JSON.',
        });
    }

    return dedupeIssues(issues);
}

function safeParseRuleJson(value: string): UrlPatternsRule[] | null {
    try {
        return parseRuleJson(value, 'URL pattern rules');
    } catch {
        return null;
    }
}

function getWebsitePolicyFieldId(path: ReadonlyArray<PropertyKey>): string {
    const firstPathElement: PropertyKey | undefined = path[0];
    if (firstPathElement === 'url') {
        return 'website_url';
    }
    if (firstPathElement === 'allowedUrlPatterns') {
        return 'allowed_url_patterns';
    }
    if (firstPathElement === 'urlPatternsRules') {
        return 'url_patterns_rules';
    }
    if (firstPathElement === 'iframeUrlPatterns') {
        return 'iframe_url_patterns';
    }
    return 'website_policy';
}

function getWebsitePolicyFieldLabel(fieldId: string): string {
    if (fieldId === 'website_url') {
        return 'Website URL';
    }
    if (fieldId === 'allowed_url_patterns') {
        return 'Allowed URL patterns';
    }
    if (fieldId === 'url_patterns_rules') {
        return 'Fetch URL pattern rules';
    }
    if (fieldId === 'iframe_url_patterns') {
        return 'Iframe URL pattern rules';
    }
    return 'Website policy';
}

function dedupeIssues(
    issues: ReadonlyArray<AllowedWebsiteTemplateValidationIssue>
): AllowedWebsiteTemplateValidationIssue[] {
    const seenKeys = new Set<string>();
    const dedupedIssues: AllowedWebsiteTemplateValidationIssue[] = [];
    for (const issue of issues) {
        const key: string = `${issue.fieldId}:${issue.message}`;
        if (seenKeys.has(key)) {
            continue;
        }
        seenKeys.add(key);
        dedupedIssues.push(issue);
    }
    return dedupedIssues;
}

export function AllowedWebsiteTemplateValidationPanel(props: {
    readonly issues: ReadonlyArray<AllowedWebsiteTemplateValidationIssue>;
}): ReactElement | null {
    if (props.issues.length === 0) {
        return null;
    }

    return (
        <Card className="border-red-300">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base text-red-700">
                    <AlertCircle className="h-4 w-4" />
                    Validation failed
                </CardTitle>
            </CardHeader>
            <CardContent>
                <ul className="space-y-1 text-sm">
                    {props.issues.map((issue) => (
                        <li key={`${issue.fieldId}:${issue.message}`}>
                            <a
                                className="font-medium text-red-700 underline"
                                href={`#${issue.fieldId}`}
                            >
                                {issue.label}
                            </a>
                            : {issue.message}
                        </li>
                    ))}
                </ul>
            </CardContent>
        </Card>
    );
}

export function AllowedWebsiteTemplateEditor(props: {
    readonly state: AllowedWebsiteTemplateEditorState;
    readonly onChange: (state: AllowedWebsiteTemplateEditorState) => void;
}): ReactElement {
    return (
        <AllowedWebsiteTemplateConfigForm
            state={props.state}
            onChange={props.onChange}
            namespaceOptions={AllowedWebsiteTemplateNamespaceOptions}
            gatewaySystemOptions={GatewayTemplateSystemOptions}
            systemOfRecordSystemOptions={SystemOfRecordTemplateSystemOptions}
            getDefaultSystemIdTextForNamespaceMode={defaultSystemIdTextForNamespaceMode}
        />
    );
}
